/**
 * Template-related type definitions for the AgentMesh marketplace
 */

export type TemplateCategory =
  | 'CUSTOMER_SUPPORT'
  | 'DATA_PROCESSING'
  | 'WORKFLOW_AUTOMATION'
  | 'CONTENT_GENERATION'
  | 'ANALYSIS'
  | 'INTEGRATION'
  | 'SALES'
  | 'MARKETING'
  | 'HR'
  | 'FINANCE'
  | 'COMMUNICATION'
  | 'GENERAL'
  | 'CODE_ASSISTANT'
  | 'RESEARCH'
  | 'CUSTOM';

export type TemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export interface TemplateAuthor {
  id: string;
  name: string;
  avatar?: string;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  status: TemplateStatus;
  isOfficial: boolean;
  version: string;
  icon?: string;
  configuration: Record<string, unknown>;
  tags: string[];
  defaultTags?: string[];
  usageCount: number;
  rating: number | null;
  ratingCount: number;
  author?: TemplateAuthor;
  forkedFromId?: string | null;
  forkCount?: number;
  workspaceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTemplateRequest {
  name: string;
  description: string;
  category: TemplateCategory;
  configuration: Record<string, unknown>;
  icon?: string;
  tags?: string[];
  isOfficial?: boolean;
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  category?: TemplateCategory;
  status?: TemplateStatus;
  configuration?: Record<string, unknown>;
  icon?: string;
  tags?: string[];
  isOfficial?: boolean;
  version?: string;
}

export interface UseTemplateResponse {
  agentId: string;
  agentName: string;
  message: string;
}

export interface TemplateListResponse {
  data: Template[];
  meta: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TemplateFilters {
  page?: number;
  limit?: number;
  category?: TemplateCategory;
  search?: string;
  isOfficial?: boolean;
  tags?: string[];
  sortBy?: 'name' | 'usageCount' | 'rating' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface CategoryInfo {
  value: TemplateCategory;
  label: string;
  description: string;
  icon: string;
  count?: number;
}

export const TEMPLATE_CATEGORIES: CategoryInfo[] = [
  {
    value: 'CUSTOMER_SUPPORT',
    label: 'Customer Support',
    description: 'Templates for customer service and support automation',
    icon: '💬',
  },
  {
    value: 'DATA_PROCESSING',
    label: 'Data Processing',
    description: 'Templates for data transformation and ETL workflows',
    icon: '📊',
  },
  {
    value: 'WORKFLOW_AUTOMATION',
    label: 'Workflow Automation',
    description: 'Templates for automating business processes',
    icon: '⚙️',
  },
  {
    value: 'CONTENT_GENERATION',
    label: 'Content Generation',
    description: 'Templates for creating and managing content',
    icon: '✍️',
  },
  {
    value: 'ANALYSIS',
    label: 'Analysis',
    description: 'Templates for data analysis and insights',
    icon: '🔍',
  },
  {
    value: 'INTEGRATION',
    label: 'Integration',
    description: 'Templates for connecting external services',
    icon: '🔌',
  },
  {
    value: 'SALES',
    label: 'Sales',
    description: 'Templates for sales automation and CRM',
    icon: '💼',
  },
  {
    value: 'MARKETING',
    label: 'Marketing',
    description: 'Templates for marketing automation campaigns',
    icon: '📣',
  },
  {
    value: 'HR',
    label: 'HR',
    description: 'Templates for human resources processes',
    icon: '👥',
  },
  {
    value: 'FINANCE',
    label: 'Finance',
    description: 'Templates for financial operations and reporting',
    icon: '💰',
  },
  {
    value: 'COMMUNICATION',
    label: 'Communication',
    description: 'Templates for messaging, notifications and outreach',
    icon: '📨',
  },
  {
    value: 'RESEARCH',
    label: 'Research',
    description: 'Templates that gather, read and summarise external sources',
    icon: '🔬',
  },
  {
    value: 'CODE_ASSISTANT',
    label: 'Code Assistant',
    description: 'Templates for code review, generation and developer workflows',
    icon: '💻',
  },
  {
    value: 'GENERAL',
    label: 'General',
    description: 'General-purpose templates',
    icon: '📦',
  },
  {
    value: 'CUSTOM',
    label: 'Custom',
    description: 'Custom templates created by users',
    icon: '🔧',
  },
];

export function getCategoryInfo(category: TemplateCategory): CategoryInfo {
  return (
    TEMPLATE_CATEGORIES.find((c) => c.value === category) || {
      value: 'CUSTOM',
      label: 'Custom',
      description: 'Custom template',
      icon: '🔧',
    }
  );
}
